import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Plug } from 'lucide-react';
import { deleteMcpServer, listMcpServers } from '../../api/mcpServers';
import EmptyState from '../../components/ui/EmptyState';
import { getApiErrorMessage } from '../../utils/errors';

const PER_PAGE = 20;

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
        active
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-slate-100 text-slate-600 border border-slate-200'
      }`}
    >
      {active ? 'Ativo' : 'Inativo'}
    </span>
  );
}

function McpServersList() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const loadItems = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const response = await listMcpServers({ page, perPage: PER_PAGE });
      setItems(response.data || []);
      setMeta(response.meta || { page: 1, per_page: PER_PAGE, total: 0 });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os servidores MCP.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Remover o servidor MCP "${item.label}"?`)) {
      return;
    }

    setDeletingId(item.id);
    setError('');
    try {
      await deleteMcpServer(item.id);
      await loadItems(meta.page);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível remover o servidor MCP.'));
    } finally {
      setDeletingId('');
    }
  };

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const hasPagination = meta.total > meta.per_page;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Servidores MCP</h1>
          <p className="text-content-muted text-sm mt-1">
            Cadastre integrações MCP da sua conta para usar em sub-agentes.
          </p>
        </div>
        <Link
          to="/mcp-servers/new"
          className="btn-primary text-sm px-4 py-2.5 shrink-0 inline-flex items-center gap-1.5"
        >
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
          Novo servidor
        </Link>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando servidores...</p>
        </div>
      ) : items.length === 0 && !error ? (
        <EmptyState
          icon={Plug}
          title="Nenhum servidor MCP cadastrado"
          description="Cadastre um servidor MCP com URL e autenticação para conectar ferramentas aos sub-agentes."
          action={(
            <Link
              to="/mcp-servers/new"
              className="btn-primary text-sm px-4 py-2.5 inline-flex items-center gap-1.5"
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              Cadastrar primeiro servidor
            </Link>
          )}
        />
      ) : (
        <>
          <div className="hidden md:block surface-card overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase">Slug</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase">URL</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase">Auth</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-content-muted uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 text-sm font-medium text-content-primary">{item.label}</td>
                    <td className="px-4 py-3 text-sm text-content-muted font-mono">{item.slug}</td>
                    <td className="px-4 py-3 text-sm text-content-muted truncate max-w-xs">{item.url}</td>
                    <td className="px-4 py-3 text-sm text-content-muted">
                      {item.auth_type}
                      {item.token_configured ? ' · token ok' : ''}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge active={item.is_active} />
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Link
                        to={`/mcp-servers/${item.id}/edit`}
                        className="text-sm text-forge-600 hover:text-forge-800"
                      >
                        Editar
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(item)}
                        disabled={deletingId === item.id}
                        className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        {deletingId === item.id ? 'Removendo...' : 'Remover'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {items.map((item) => (
              <div key={item.id} className="surface-card p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-content-primary">{item.label}</p>
                    <p className="text-xs text-content-muted font-mono mt-1">{item.slug}</p>
                  </div>
                  <StatusBadge active={item.is_active} />
                </div>
                <p className="text-sm text-content-muted break-all">{item.url}</p>
                <div className="flex gap-3">
                  <Link
                    to={`/mcp-servers/${item.id}/edit`}
                    className="text-sm text-forge-600 hover:text-forge-800"
                  >
                    Editar
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    disabled={deletingId === item.id}
                    className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>

          {hasPagination && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-content-subtle">
                {meta.total} servidor{meta.total !== 1 ? 'es' : ''} · página {meta.page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={meta.page <= 1}
                  onClick={() => loadItems(meta.page - 1)}
                  className="btn-secondary text-sm px-3 py-2 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={meta.page >= totalPages}
                  onClick={() => loadItems(meta.page + 1)}
                  className="btn-secondary text-sm px-3 py-2 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default McpServersList;
